import { useEffect, useState } from 'react';

interface ProgressBarProps {
  durationMs: number;
  isComplete: boolean;
  className?: string;
}

export function ProgressBar({ durationMs, isComplete, className = '' }: ProgressBarProps) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (isComplete) {
      setProgress(100);
      return;
    }

    // Calculate how far to go (max ~92% before complete signal)
    const targetProgress = 92;
    const intervalMs = 100; // Update every 100ms
    const totalSteps = durationMs / intervalMs;
    const incrementPerStep = targetProgress / totalSteps;

    const timer = setInterval(() => {
      setProgress((prev) => {
        const next = prev + incrementPerStep;
        if (next >= targetProgress) {
          clearInterval(timer);
          return targetProgress;
        }
        return next;
      });
    }, intervalMs);

    return () => clearInterval(timer);
  }, [durationMs, isComplete]);

  return (
    <div className={`w-full ${className}`}>
      {/* Progress bar container */}
      <div className="relative h-2 bg-muted/50 rounded-full overflow-hidden">
        {/* Progress fill */}
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary to-primary/80 rounded-full transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
        />

        {/* Animated pulse effect */}
        <div
          className="absolute inset-y-0 left-0 rounded-full overflow-hidden animate-pulse"
          style={{ width: `${progress}%` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        </div>
      </div>

      {/* Percentage text */}
      <div className="flex justify-between items-center mt-2">
        <span className="text-xs text-muted-foreground">
          {isComplete ? 'Complete!' : 'Generating your design...'}
        </span>
        <span className="text-xs font-medium text-foreground tabular-nums">
          {Math.round(progress)}%
        </span>
      </div>
    </div>
  );
}
